import React, {useCallback, useEffect, useState} from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {Button, DialogActions, DialogContent} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import Call from "../../hocs/call";
import ConfirmationDialog from "../confirmation-dialog";
import CustomJsonForms from "../custom-json-forms";
import FullscreenDialog from "../fullscreen-dialog";
import MaterialTable from "../material-table";
import SettingsDialog from "../settings-dialog";
import {appSelector} from "../../state/app/appSelectors";
import {nodeIdSelector} from "../../state/node/nodeSelectors";
import {
  createAgent,
  deleteAgent,
  fetchAgentList,
  fetchAgentSchema,
  getAgent,
  hideAgent,
  hideAgentList,
  showAgent,
  updateAgent
} from "../../state/package-manager/packageManagerActions";
import {agentsManagerSelector} from "../../state/package-manager/packageManagerSelectors";
import {localizeI18nObj} from "../../utils/i18n";

const AgentsManager = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {language, languages} = useSelector(appSelector);

  const nodeId = useSelector(nodeIdSelector);
  const {isVisible, agents, schema, uiSchema, agentId, agent} = useSelector(agentsManagerSelector);

  const [agentToDelete, setAgentToDelete] = useState(null);

  const [data, setData] = useState(null);
  const [errors, setErrors] = useState(null);
  const [showValidation, setShowValidation] = useState(false);

  const handleFetchAgents = useCallback(() => {
    dispatch(fetchAgentList(nodeId));
  }, [dispatch, nodeId]);

  const handleCloseAgentsDialog = useCallback(() => {
    dispatch(hideAgentList());
  }, [dispatch]);

  const handleCloseAgentDialog = useCallback(() => {
    dispatch(hideAgent());
  }, [dispatch]);

  const handleCreateAgent = useCallback(() => {
    dispatch(showAgent());
  }, [dispatch]);

  const handleEditAgent = useCallback(
    (_, {__Guid, title}) => {
      dispatch(getAgent(nodeId, __Guid, title));
    },
    [dispatch, nodeId]
  );

  const handleFetchAgentSchema = useCallback(() => {
    dispatch(fetchAgentSchema(nodeId));
  }, [dispatch, nodeId]);

  const handleSubmitAgent = useCallback(() => {
    if ((errors || []).length > 0) {
      setShowValidation(true);
    } else {
      setShowValidation(false);
      if (agentId) {
        dispatch(updateAgent(nodeId, agentId, data));
      } else {
        dispatch(createAgent(nodeId, data));
      }
    }
  }, [dispatch, errors, agentId, nodeId, data]);

  const handleStartDelete = useCallback((_, agent) => {
    setAgentToDelete(agent);
  }, []);

  const handleAbortDelete = useCallback(() => {
    setAgentToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    dispatch(deleteAgent(nodeId, agentToDelete.__Guid, agentToDelete.title));
    setAgentToDelete(null);
  }, [dispatch, nodeId, agentToDelete]);

  useEffect(() => {
    setData(agent);
    setErrors(null);
    setShowValidation(false);

    return () => {
      setData(null);
      setErrors(null);
      setShowValidation(false);
    };
  }, [agent]);

  if (!nodeId || !isVisible) {
    return null;
  }

  return (
    <SettingsDialog
      title={t("modules.packageManager.agentsManager.dialog.title")}
      open={isVisible}
      onClose={handleCloseAgentsDialog}
      className="agents-manager__agents-dialog"
    >
      <Call cb={handleFetchAgents} disabled={agents !== null}>
        <MaterialTable
          id={"agents-manager__table"}
          data={agents || []}
          rightActions={[
            {
              id: "agents-manager__table__actions__create",
              label: t("modules.packageManager.agentsManager.actions.create.label"),
              startIcon: <AddIcon />,
              onClick: handleCreateAgent
            }
          ]}
          actions={[
            {
              icon: <EditIcon />,
              tooltip: t("modules.packageManager.agentsManager.actions.edit.tooltip"),
              onClick: handleEditAgent,
              className: "agents-manager__table__actions__edit"
            },
            {
              icon: <DeleteIcon />,
              tooltip: t("modules.packageManager.agentsManager.actions.delete.tooltip"),
              onClick: handleStartDelete,
              className: "agents-manager__table__actions__delete"
            }
          ]}
          columns={[
            {
              field: "__Guid",
              title: t("modules.packageManager.agentsManager.columns.id.label"),
              hidden: true,
              getClassName: () => "agents-manager__table__columns__id"
            },
            {
              field: "title",
              title: t("modules.packageManager.agentsManager.columns.title.label"),
              render: ({title}) => localizeI18nObj(title, language, languages),
              getClassName: () => "agents-manager__table__columns__id"
            }
          ]}
        />

        <ConfirmationDialog
          title={t("modules.packageManager.agentsManager.deleteDialog.title")}
          content={t("modules.packageManager.agentsManager.deleteDialog.description")}
          open={agentToDelete !== null}
          onClose={handleAbortDelete}
          onConfirm={handleConfirmDelete}
          className="agents-manager__delete-agent-dialog"
        />

        <FullscreenDialog
          open={agent !== null}
          title={
            agentId
              ? t("modules.packageManager.agentsManager.editDialog.title")
              : t("modules.packageManager.agentsManager.createDialog.title")
          }
          onClose={handleCloseAgentDialog}
          className="agents-manager__agent-dialog"
        >
          <DialogContent>
            <Call cb={handleFetchAgentSchema} disabled={schema !== null}>
              {schema && uiSchema && (
                <CustomJsonForms
                  schema={schema}
                  uischema={uiSchema}
                  data={data}
                  setData={setData}
                  errors={errors}
                  setErrors={setErrors}
                  showValidation={showValidation}
                  i18nPrefix={"modules.packageManager.agentsManager"}
                />
              )}
            </Call>
          </DialogContent>
          <DialogActions>
            <Button className="agents-manager__agent-dialog__cancel-btn" onClick={handleCloseAgentDialog}>
              {t("commons.confirm.cancel")}
            </Button>
            <Button className="agents-manager__agent-dialog__submit-btn" onClick={handleSubmitAgent}>
              {t("commons.confirm.submit")}
            </Button>
          </DialogActions>
        </FullscreenDialog>
      </Call>
    </SettingsDialog>
  );
};

export default AgentsManager;
